/**
 * Builds a reverse adjacency list from a dependency graph.
 * For each entity, the reverse graph maps it to the entities that depend on it.
 *
 * @param dependencyGraph - Map of entity ID → array of dependency IDs
 * @returns Map of entity ID → array of dependent entity IDs
 */
export const buildReverseGraph = (
  dependencyGraph: Map<string, string[]>,
): Map<string, string[]> => {
  const reverseGraph = new Map<string, string[]>();

  Array.from(dependencyGraph.entries()).forEach(([entityId, deps]) => {
    deps.forEach((depId) => {
      const dependents = reverseGraph.get(depId) ?? [];
      dependents.push(entityId);
      reverseGraph.set(depId, dependents);
    });
  });

  return reverseGraph;
};

/**
 * Given a failed entity ID and a pre-built reverse graph, computes the set of
 * transitively dependent entity IDs that should be marked as skipped.
 *
 * Use {@link buildReverseGraph} to build the reverse graph from a dependency graph,
 * then call this function for each failure to avoid rebuilding the reverse graph
 * on every call.
 *
 * @param failedEntityId - The ID of the entity that failed
 * @param reverseGraph - Map of entity ID → array of dependent entity IDs
 * @returns Set of entity IDs that should be marked as skipped
 */
export const getCascadingFailureIdsFromReverseGraph = (
  failedEntityId: string,
  reverseGraph: Map<string, string[]>,
): Set<string> => {
  const skippedIds = new Set<string>();
  const queue = [failedEntityId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const dependents = reverseGraph.get(currentId) ?? [];

    dependents
      .filter((dependentId) => !skippedIds.has(dependentId))
      .forEach((dependentId) => {
        skippedIds.add(dependentId);
        queue.push(dependentId);
      });
  }

  return skippedIds;
};

/**
 * Given a failed entity ID and a dependency graph, computes the set of
 * transitively dependent entity IDs that should be marked as skipped.
 *
 * The dependency graph maps each entity ID to its direct dependency IDs.
 * An entity "depends on" another if it appears in that entity's dependency list.
 * When an entity fails, all entities that transitively depend on it
 * (directly or through a chain) must be skipped.
 *
 * For repeated calls with the same graph, prefer using {@link buildReverseGraph}
 * and {@link getCascadingFailureIdsFromReverseGraph} to avoid rebuilding
 * the reverse graph on each call.
 *
 * @param failedEntityId - The ID of the entity that failed
 * @param dependencyGraph - Map of entity ID → array of dependency IDs
 * @returns Set of entity IDs that should be marked as skipped
 */
export const getCascadingFailureIds = (
  failedEntityId: string,
  dependencyGraph: Map<string, string[]>,
): Set<string> => {
  const reverseGraph = buildReverseGraph(dependencyGraph);
  return getCascadingFailureIdsFromReverseGraph(failedEntityId, reverseGraph);
};
